/**
 * ExpenseCategoriesTab — Admin de categorías de gasto con mapeo a cuenta GL.
 * Cada categoría se usa en recibos de egreso para rutear el asiento a la
 * cuenta contable correcta en lugar de caer al mapping genérico "misc_expense".
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Plus, Edit2, Trash2, Tag } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

export default function ExpenseCategoriesTab() {
  const [list, setList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null); // { id?, name, account_code, description }
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/expense-categories"),
        api.get("/accounting/coa?type=gasto"),
      ]);
      setList(c.data || []);
      const accs = (a.data || []).filter((x) => !x.is_header);
      setAccounts(accs);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!dlg?.name?.trim()) return toast.error("El nombre es obligatorio");
    if (!dlg?.account_code) return toast.error("Selecciona una cuenta contable");
    try {
      if (dlg.id) {
        await api.put(`/expense-categories/${dlg.id}`, {
          name: dlg.name, account_code: dlg.account_code, description: dlg.description || "",
        });
        toast.success("Categoría actualizada");
      } else {
        await api.post("/expense-categories", {
          name: dlg.name, account_code: dlg.account_code, description: dlg.description || "",
        });
        toast.success("Categoría creada");
      }
      setDlg(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (cat) => {
    const ok = await confirm({
      title: "Eliminar categoría",
      message: `¿Eliminar "${cat.name}"? Los recibos existentes mantienen su asiento, pero futuros egresos con este nombre caerán a Otros Gastos (5210).`,
      confirmText: "Eliminar",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/expense-categories/${cat.id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="expense-categories-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Tag className="w-4 h-4 text-primary" /> Categorías de gasto
          </h3>
          <p className="text-sm text-muted-foreground">
            Cada categoría se mapea a una cuenta contable específica. Al crear un recibo de egreso,
            el asiento usará la cuenta de la categoría; si no hay match, cae a <b>5210 Otros Gastos</b>.
          </p>
        </div>
        <Button onClick={() => setDlg({ name: "", account_code: "", description: "" })} data-testid="new-expense-cat-btn">
          <Plus className="w-4 h-4 mr-1" />Nueva categoría
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2 text-left">Nombre</th>
                <th className="p-2 text-left">Cuenta GL</th>
                <th className="p-2 text-left">Descripción</th>
                <th className="p-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">
                  Sin categorías. Crea la primera (ej. Combustible, Alquiler, Papelería) para rutear los gastos a la cuenta correcta.
                </td></tr>
              ) : list.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`expense-cat-row-${c.id}`}>
                  <td className="p-2 font-medium">{c.name}</td>
                  <td className="p-2"><Badge variant="outline">{c.account_code}</Badge> <span className="text-xs text-muted-foreground">{c.account_name}</span></td>
                  <td className="p-2 text-muted-foreground">{c.description || "—"}</td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDlg({ id: c.id, name: c.name, account_code: c.account_code, description: c.description || "" })} data-testid={`edit-cat-${c.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`del-cat-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="expense-cat-dialog">
          <DialogHeader>
            <DialogTitle>{dlg?.id ? "Editar categoría" : "Nueva categoría de gasto"}</DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div>
                <Label>Nombre</Label>
                <Input value={dlg.name} onChange={(e) => setDlg({ ...dlg, name: e.target.value })} placeholder="Combustible, Alquiler, Papelería…" data-testid="cat-name-input" />
              </div>
              <div>
                <Label>Cuenta contable</Label>
                <Select value={dlg.account_code} onValueChange={(v) => setDlg({ ...dlg, account_code: v })}>
                  <SelectTrigger data-testid="cat-account-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.code} value={a.code}>
                        {a.code} — {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Input value={dlg.description} onChange={(e) => setDlg({ ...dlg, description: e.target.value })} placeholder="Qué incluye esta categoría" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button onClick={save} data-testid="save-cat-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BackfillBlock />
    </div>
  );
}

// ─── Backfill admin ────────────────────────────────────────────────────
function BackfillBlock() {
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const confirm = useConfirm();

  const loadPreview = async () => {
    try {
      const r = await api.get("/admin/backfill/receipts/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { loadPreview(); }, []);

  const run = async () => {
    const ok = await confirm({
      title: "Sanear libro contable",
      message: `Se generarán asientos retroactivos para ${preview?.orphan_without_entry ?? 0} recibo(s) sin asiento y ${preview?.orphan_voids_without_reverse ?? 0} anulado(s) sin reverso. Esta acción es segura y reversible desde Contabilidad.`,
      confirmText: "Ejecutar backfill",
    });
    if (!ok) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/backfill/receipts", { dry_run: false, limit: 2000 });
      setResult(r.data);
      toast.success("Backfill completado");
      loadPreview();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-8 border-t pt-4 space-y-3" data-testid="backfill-block">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Loader2 className={`w-4 h-4 ${running ? "animate-spin text-primary" : "text-muted-foreground"}`} />
        Saneamiento del libro contable
      </h3>
      <p className="text-sm text-muted-foreground">
        Genera asientos contables retroactivos para recibos antiguos que no los tienen.
        Útil tras actualizaciones del sistema. Es idempotente — solo toca lo que falta.
      </p>
      {preview && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
          <div>Recibos totales: <b>{preview.total_receipts}</b></div>
          <div>Sin asiento contable: <b className={preview.orphan_without_entry ? "text-amber-700 dark:text-amber-400" : "text-emerald-700"}>{preview.orphan_without_entry}</b></div>
          <div>Anulados sin reverso: <b className={preview.orphan_voids_without_reverse ? "text-amber-700 dark:text-amber-400" : "text-emerald-700"}>{preview.orphan_voids_without_reverse}</b></div>
        </div>
      )}
      <Button
        onClick={run}
        disabled={running || !preview || (preview.orphan_without_entry + preview.orphan_voids_without_reverse === 0)}
        data-testid="run-backfill-btn"
      >
        {running ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Procesando…</> : "Ejecutar backfill"}
      </Button>
      {result && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm">
          <div className="font-bold text-emerald-700 dark:text-emerald-400 mb-1">Resultado</div>
          <div>Procesados: <b>{result.total_scanned}</b></div>
          <div>Ingresos asentados: <b>{result.created_income}</b></div>
          <div>Egresos asentados: <b>{result.created_expense}</b></div>
          <div>Reversos de anulados: <b>{result.voids_reversed}</b></div>
          {result.failed > 0 && <div className="text-rose-600">Fallidos: {result.failed}</div>}
        </div>
      )}
    </div>
  );
}
